import React, { useState, useEffect } from "react";
import { FiChevronDown, FiChevronRight, FiPlus, FiTrash2, FiX, FiEdit2, FiList, FiGitBranch } from "react-icons/fi";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const getEmissionMultiplier = (type, equityShareNum, approach) => {
  const share = isNaN(equityShareNum) || equityShareNum === null ? 100 : Number(equityShareNum);
  const fraction = share / 100;

  if (type === 'Fixed Asset Investment') {
    return 0;
  }

  if (approach === 'Financial Control') {
    if (type === 'Subsidiary') {
      return 1.0; // 100% ownership of GHG emissions
    } else if (type === 'Affiliated Company') {
      return 0; // 0%
    } else if (type === 'Joint Venture') {
      return fraction; // Equity share of GHG emissions
    }
    return 1.0;
  } else {
    // Equity Share Approach
    if (type === 'Subsidiary' || type === 'Affiliated Company' || type === 'Joint Venture') {
      return fraction; // Equity share of GHG emissions
    }
    return 0;
  }
};

const OrgChartNode = ({ node, consolidationApproach, onAdd, onDelete, onEdit }) => {
  const multiplier = getEmissionMultiplier(node.type, node.equityShare, consolidationApproach);
  const inclusionPercent = (multiplier * 100).toFixed(0);

  return (
    <div className="flex flex-col items-center">
      <div className="group relative bg-white dark:bg-gray-800 border-2 border-slate-200 dark:border-slate-700 w-60 rounded-xl shadow-sm p-4 transition hover:border-blue-400 dark:hover:border-blue-500 mt-2 mb-2 z-10 hover:shadow-md">
        <h3 className="font-bold text-gray-800 dark:text-gray-100 text-base truncate mb-1">{node.name}</h3>
        
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          <span className="inline-block px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-semibold uppercase tracking-wide rounded-full">
            {node.type}
          </span>
          {node.type !== 'Company' && (
            <span className="inline-block px-2 py-0.5 bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-xs font-medium rounded-full">
              Share: {node.equityShare ?? 100}%
            </span>
          )}
        </div>

        {node.type !== 'Company' && (
          <div className="mb-2 text-xs font-medium text-slate-500 dark:text-slate-400 flex justify-between items-center bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
            <span>GHG Contrib:</span>
            <span className={multiplier > 0 ? "text-green-600 font-bold dark:text-green-400" : "text-gray-400 font-semibold"}>
              {inclusionPercent}%
            </span>
          </div>
        )}

        <div className="text-left text-xs space-y-1.5 bg-slate-50 dark:bg-slate-900/50 p-2.5 rounded-lg border border-slate-100 dark:border-slate-700/50">
          <div className="flex justify-between items-center"><span className="text-slate-500">Scope 1</span> <span className="font-medium text-orange-600 dark:text-orange-400">{Number(node.scope[0] || 0).toFixed(2)}</span></div>
          <div className="flex justify-between items-center"><span className="text-slate-500">Scope 2</span> <span className="font-medium text-blue-600 dark:text-blue-400">{Number(node.scope[1] || 0).toFixed(2)}</span></div>
          <div className="flex justify-between items-center"><span className="text-slate-500">Scope 3</span> <span className="font-medium text-green-600 dark:text-green-400">{Number(node.scope[2] || 0).toFixed(2)}</span></div>
          <div className="border-t border-slate-200 dark:border-slate-700 mt-1.5 pt-1.5 flex justify-between items-center"><span className="font-semibold text-slate-700 dark:text-slate-300">Total Eq</span> <span className="font-bold text-sm text-gray-900 dark:text-white">{Number(node.total || 0).toFixed(2)}</span></div>
        </div>

        <div className="absolute -top-3 -right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-1.5 z-20">
          <button onClick={() => onAdd(node.id)} className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-blue-600" title="Add Sub-Entity"><FiPlus size={16}/></button>
          {node.type !== 'Company' && (
            <>
              <button onClick={() => onEdit(node)} className="bg-amber-500 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-amber-600" title="Edit Entity"><FiEdit2 size={16}/></button>
              <button onClick={() => onDelete(node.id)} className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-red-600" title="Delete Entity"><FiTrash2 size={16}/></button>
            </>
          )}
        </div>
      </div>

      {node.children && node.children.length > 0 && (
        <div className="flex flex-col items-center">
          <div className="w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
          
          <div className="flex flex-row justify-center relative">
            {node.children.map((child, index) => {
              const isFirst = index === 0;
              const isLast = index === node.children.length - 1;
              const isOnly = node.children.length === 1;

              return (
                <div key={child.id} className="relative flex flex-col items-center px-4">
                  {!isOnly && (
                    <div className={`absolute top-0 h-px bg-slate-300 dark:bg-slate-600 -z-10
                          ${isFirst ? 'left-1/2 right-0' : isLast ? 'left-0 right-1/2' : 'left-0 right-0'}`}
                    ></div>
                  )}
                  <div className="w-px h-6 bg-slate-300 dark:bg-slate-600 -z-10"></div>
                  
                  <OrgChartNode node={child} consolidationApproach={consolidationApproach} onAdd={onAdd} onDelete={onDelete} onEdit={onEdit} />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const TreeTableRow = ({ node, level, isExpanded, consolidationApproach, onToggle, onAdd, onEdit, onDelete }) => {
  const hasChildren = node.children && node.children.length > 0;
  const multiplier = getEmissionMultiplier(node.type, node.equityShare, consolidationApproach);
  const inclusionPercent = (multiplier * 100).toFixed(0);

  return (
    <tr className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition text-sm">
      <td className="p-3">
        <div className="flex items-center" style={{ paddingLeft: `${level * 2}rem` }}>
          {hasChildren ? (
            <button onClick={() => onToggle(node.id)} className="mr-2 text-gray-500 hover:text-blue-500 rounded p-1">
              {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
            </button>
          ) : (
            <span className="w-6 mr-2 inline-block"></span>
          )}
          <span className="font-medium text-gray-800 dark:text-gray-200">{node.name}</span>
        </div>
      </td>
      <td className="p-3 text-gray-600 dark:text-gray-400">{node.type}</td>
      <td className="p-3 text-purple-600 dark:text-purple-400 font-medium">{node.type !== 'Company' ? `${node.equityShare ?? 100}%` : '-'}</td>
      <td className="p-3">
        {node.type !== 'Company' ? (
          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${multiplier > 0 ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
            {inclusionPercent}%
          </span>
        ) : '-'}
      </td>
      <td className="p-3 font-semibold text-gray-800 dark:text-gray-200">{Number(node.total || 0).toFixed(2)}</td>
      <td className="p-3 text-orange-500">{Number(node.scope[0] || 0).toFixed(2)}</td>
      <td className="p-3 text-blue-500">{Number(node.scope[1] || 0).toFixed(2)}</td>
      <td className="p-3 text-green-500">{Number(node.scope[2] || 0).toFixed(2)}</td>
      <td className="p-3 flex items-center space-x-2">
        <button onClick={() => onAdd(node.id)} className="text-blue-500 hover:text-blue-700 mx-1" title="Add Sub-Entity"><FiPlus /></button>
        {node.type !== 'Company' && (
          <>
            <button onClick={() => onEdit(node)} className="text-gray-500 hover:text-gray-700 mx-1" title="Edit Entity"><FiEdit2 /></button>
            <button onClick={() => onDelete(node.id)} className="text-red-500 hover:text-red-700 mx-1" title="Delete Entity"><FiTrash2 /></button>
          </>
        )}
      </td>
    </tr>
  );
};

export default function OrgBoundaryTreeTable() {
  const [treeData, setTreeData] = useState([]);
  const [entitiesData, setEntitiesData] = useState([]);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [consolidationApproach, setConsolidationApproach] = useState('Financial Control'); // 'Financial Control' or 'Equity Share'
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'table'
  const axiosPrivate = useAxiosPrivate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeParentId, setActiveParentId] = useState(null);
  const [activeEditId, setActiveEditId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'Subsidiary',
    equityShare: '100',
    scope1: '0',
    scope2: '0',
    scope3: '0'
  });

  const fetchBoundaries = async () => {
    try {
      const response = await axiosPrivate.get('/orgBoundary');
      setEntitiesData(response.data);
    } catch (error) {
      console.error("Error fetching boundaries:", error);
    }
  };

  useEffect(() => {
    fetchBoundaries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const buildTree = (entities, parentId = null) => {
      return entities
        .filter(entity => entity.parentId === parentId)
        .map(entity => {
          const children = buildTree(entities, entity.id);

          const childTotal = children.reduce((sum, child) => {
            const mult = getEmissionMultiplier(child.type, child.equityShare, consolidationApproach);
            return sum + (child.total || 0) * mult;
          }, 0);

          const childScope0 = children.reduce((sum, child) => {
            const mult = getEmissionMultiplier(child.type, child.equityShare, consolidationApproach);
            return sum + (child.scope?.[0] || 0) * mult;
          }, 0);

          const childScope1 = children.reduce((sum, child) => {
            const mult = getEmissionMultiplier(child.type, child.equityShare, consolidationApproach);
            return sum + (child.scope?.[1] || 0) * mult;
          }, 0);

          const childScope2 = children.reduce((sum, child) => {
            const mult = getEmissionMultiplier(child.type, child.equityShare, consolidationApproach);
            return sum + (child.scope?.[2] || 0) * mult;
          }, 0);

          const selfScope0 = Number(entity.scope?.[0] || 0);
          const selfScope1 = Number(entity.scope?.[1] || 0);
          const selfScope2 = Number(entity.scope?.[2] || 0);

          return {
            ...entity,
            total: selfScope0 + selfScope1 + selfScope2 + childTotal,
            scope: [
              selfScope0 + childScope0,
              selfScope1 + childScope1,
              selfScope2 + childScope2,
            ],
            children
          };
        });
    };

    const hierarchalData = buildTree(entitiesData);
    setTreeData(hierarchalData);

    if (expandedRows.size === 0 && hierarchalData.length > 0) {
      const topIds = hierarchalData.map(node => node.id);
      setExpandedRows(new Set(topIds));
    }
  }, [entitiesData, consolidationApproach]);

  const toggleRow = (id) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleTypeChange = (newType) => {
    let defaultShare = '100';
    if (newType === 'Affiliated Company') defaultShare = '30';
    else if (newType === 'Joint Venture') defaultShare = '50';
    else if (newType === 'Fixed Asset Investment') defaultShare = '0';
    
    setFormData(prev => ({
      ...prev,
      type: newType,
      equityShare: defaultShare
    }));
  };

  const handleAddChild = (parentId) => {
    setActiveParentId(parentId);
    setActiveEditId(null);
    setFormData({
      name: '',
      type: 'Subsidiary',
      equityShare: '100',
      scope1: '0',
      scope2: '0',
      scope3: '0'
    });
    setIsModalOpen(true);
  };

  const handleEdit = (node) => {
    setActiveEditId(node.id);
    setActiveParentId(node.parentId);
    const originalEntity = entitiesData.find(e => e.id === node.id);
    const scopeData = originalEntity ? originalEntity.scope : node.scope;
    
    setFormData({
      name: node.name,
      type: node.type,
      equityShare: originalEntity && originalEntity.equityShare !== undefined ? originalEntity.equityShare.toString() : '100',
      scope1: scopeData[0] !== undefined ? scopeData[0].toString() : '0',
      scope2: scopeData[1] !== undefined ? scopeData[1].toString() : '0',
      scope3: scopeData[2] !== undefined ? scopeData[2].toString() : '0'
    });
    setIsModalOpen(true);
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    const sc1 = parseFloat(formData.scope1) || 0;
    const sc2 = parseFloat(formData.scope2) || 0;
    const sc3 = parseFloat(formData.scope3) || 0;
    const eqShare = parseFloat(formData.equityShare) || 0;

    try {
      if (activeEditId) {
        await axiosPrivate.put(`/orgBoundary/${activeEditId}`, {
          name: formData.name,
          type: formData.type,
          equityShare: eqShare,
          scope1: sc1,
          scope2: sc2,
          scope3: sc3
        });
      } else {
        await axiosPrivate.post('/orgBoundary', {
          name: formData.name,
          type: formData.type,
          equityShare: eqShare,
          parentId: activeParentId,
          scope1: sc1,
          scope2: sc2,
          scope3: sc3
        });
        if (activeParentId) {
          setExpandedRows(prev => new Set(prev).add(activeParentId));
        }
      }
      fetchBoundaries();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error saving entity:", error);
      alert(error.response?.data?.error || "Failed to save entity");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this entity and all its sub-entities?")) {
      try {
        await axiosPrivate.delete(`/orgBoundary/${id}`);
        fetchBoundaries();
      } catch (error) {
        console.error("Error deleting entity:", error);
        alert(error.response?.data?.error || "Failed to delete entity");
      }
    }
  };

  const renderTreeRows = (nodes, level = 0) => {
    let rows = [];
    nodes.forEach(node => {
      const isExpanded = expandedRows.has(node.id);
      rows.push(
        <TreeTableRow
          key={node.id}
          node={node}
          level={level}
          isExpanded={isExpanded}
          consolidationApproach={consolidationApproach}
          onToggle={toggleRow}
          onAdd={handleAddChild}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      );
      if (isExpanded && node.children && node.children.length > 0) {
        rows = rows.concat(renderTreeRows(node.children, level + 1));
      }
    });
    return rows;
  };

  return (
    <div className="flex flex-col p-4 md:p-8 w-full max-w-full mx-auto overflow-auto h-full space-y-8 bg-gray-50/50 dark:bg-gray-900/50">
      <div className="flex items-center justify-between flex-wrap gap-4 sticky left-0 right-0">
        <div>
          <h2 className="text-2xl font-bold dark:text-white">
            Organizational Boundary
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Consolidation Approach: <span className="font-semibold text-blue-600 dark:text-blue-400">{consolidationApproach}</span>
          </p>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          {/* Approach Selection Toggle */}
          <div className="flex bg-gray-200 dark:bg-gray-700 p-1 rounded-lg text-xs font-medium">
            <button
              onClick={() => setConsolidationApproach('Financial Control')}
              className={`px-3 py-1.5 rounded-md transition ${
                consolidationApproach === 'Financial Control'
                  ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 font-bold shadow'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900'
              }`}
            >
              Financial Control
            </button>
            <button
              onClick={() => setConsolidationApproach('Equity Share')}
              className={`px-3 py-1.5 rounded-md transition ${
                consolidationApproach === 'Equity Share'
                  ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 font-bold shadow'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900'
              }`}
            >
              Equity Share
            </button>
          </div>

          {/* View Mode Switcher */}
          <div className="flex bg-gray-200 dark:bg-gray-700 p-1 rounded-lg text-xs font-medium">
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition ${
                viewMode === 'table'
                  ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 font-bold shadow'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900'
              }`}
            >
              <FiList size={14} /> Table Mode
            </button>
            <button
              onClick={() => setViewMode('tree')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition ${
                viewMode === 'tree'
                  ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 font-bold shadow'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900'
              }`}
            >
              <FiGitBranch size={14} /> Tree Mode
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'table' ? (
        <div className="bg-white dark:bg-darkbg4 rounded-xl shadow border dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-sm uppercase tracking-wider">
                  <th className="p-4 font-semibold">Entity Name</th>
                  <th className="p-4 font-semibold">Type</th>
                  <th className="p-4 font-semibold">Equity Share</th>
                  <th className="p-4 font-semibold">GHG Contrib</th>
                  <th className="p-4 font-semibold">Total Emission</th>
                  <th className="p-4 font-semibold">Scope 1</th>
                  <th className="p-4 font-semibold">Scope 2</th>
                  <th className="p-4 font-semibold">Scope 3</th>
                  <th className="p-4 font-semibold w-32">Actions</th>
                </tr>
              </thead>
              <tbody>
                {treeData.length > 0 ? (
                  renderTreeRows(treeData)
                ) : (
                  <tr>
                    <td colSpan="9" className="p-6 text-center text-gray-500">
                      No organizational entities found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="min-w-min flex justify-center py-10 px-4 bg-white dark:bg-darkbg4 border dark:border-gray-700 rounded-xl shadow-inner overflow-x-auto">
          <div className="flex gap-16 items-start">
            {treeData.length > 0 ? (
              treeData.map(rootNode => (
                <OrgChartNode
                  key={rootNode.id}
                  node={rootNode}
                  consolidationApproach={consolidationApproach}
                  onAdd={handleAddChild}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                />
              ))
            ) : (
              <div className="text-gray-500 py-10 text-center">
                No organizational entities found.
              </div>
            )}
          </div>
        </div>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-6 border-b border-gray-200 dark:border-gray-700 pb-3">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {activeEditId ? 'Edit Entity' : 'Add Sub-Entity'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-full p-1 hover:bg-gray-100 dark:hover:bg-gray-700">
                <FiX size={24} />
              </button>
            </div>
            <form onSubmit={handleModalSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Entity Name</label>
                <input required type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition" placeholder="e.g. New Subsidiary" />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Joint Operation Type</label>
                  <select value={formData.type} onChange={e => handleTypeChange(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition text-sm">
                    <option value="Subsidiary">Subsidiary</option>
                    <option value="Affiliated Company">Affiliated Company</option>
                    <option value="Joint Venture">Joint Venture</option>
                    <option value="Fixed Asset Investment">Fixed Asset Investment</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Equity Share (%)</label>
                  <input type="number" min="0" max="100" step="any" value={formData.equityShare} onChange={e => setFormData({ ...formData, equityShare: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Scope 1</label>
                  <input type="number" step="any" value={formData.scope1} onChange={e => setFormData({ ...formData, scope1: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Scope 2</label>
                  <input type="number" step="any" value={formData.scope2} onChange={e => setFormData({ ...formData, scope2: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Scope 3</label>
                  <input type="number" step="any" value={formData.scope3} onChange={e => setFormData({ ...formData, scope3: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition" />
                </div>
              </div>

              <div className="pt-6 flex justify-end space-x-3">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium transition">Cancel</button>
                <button type="submit" className="px-5 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 shadow-md transition">Save Entity</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
